import { useEffect } from 'react';

const SITE_URL = 'https://bolaoelite.com.br';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  canonical?: string;
  noIndex?: boolean;
  jsonLd?: object | object[];
  /** Código de verificação do Google Search Console (conteúdo da meta tag) */
  googleVerificationCode?: string;
  /** Release C: hreflang alternates (pt-BR/en/es + x-default). */
  alternates?: Array<{ hreflang: string; href: string }>;
  /** Release C: locale BCP-47 da página (og:locale). Default pt_BR. */
  locale?: 'pt-BR' | 'en' | 'es';
}

const OG_LOCALE: Record<string, string> = {
  'pt-BR': 'pt_BR',
  en: 'en_US',
  es: 'es_ES',
};

/**
 * Componente de SEO para o Bolão Elite.
 *
 * Atualiza meta tags via DOM manipulation (substituicao, nao adicao). Isso
 * garante que a meta tag final tenha valor unico — sem competicao entre a
 * estatica do index.html e a dinamica do React.
 *
 * Por que nao usar React 19 head hoisting nativo via JSX? Porque o hoisting
 * apenas ADICIONA novas meta tags ao <head>, nao SUBSTITUI as existentes
 * (exceto <title>). O resultado seria duas tags com mesmo nome — Google e
 * outros crawlers escolhem qualquer uma das duas, geralmente a primeira (a
 * estatica do index.html, generica).
 *
 * Cobertura:
 * - <title>: via JSX (React 19 dedupa <title> automaticamente)
 * - meta description, keywords, robots, googlebot: via DOM
 * - link canonical: via DOM
 * - Open Graph (og:*): via DOM
 * - Twitter Card (twitter:*): via DOM
 * - JSON-LD Structured Data: via DOM (multiplos <script> ld+json)
 * - Google Search Console verification: via DOM
 */
export function SEO({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage = `${SITE_URL}/og-image.png`,
  ogUrl,
  canonical,
  noIndex = false,
  jsonLd,
  googleVerificationCode,
  alternates,
  locale = 'pt-BR',
}: SEOProps) {
  const resolvedOgTitle = ogTitle ?? title;
  const resolvedOgDesc = ogDescription ?? description;
  const resolvedOgUrl = ogUrl ?? SITE_URL;
  const resolvedCanonical = canonical ?? resolvedOgUrl;
  const robotsValue = noIndex ? 'noindex,nofollow' : 'index,follow';

  // Atualiza/cria meta tags e link canonical no <head>
  useEffect(() => {
    document.title = title;
    upsertMeta('name', 'description', description);
    if (keywords) upsertMeta('name', 'keywords', keywords);
    upsertMeta('name', 'author', 'Bolão Elite');
    upsertMeta('name', 'robots', robotsValue);
    upsertMeta('name', 'googlebot', robotsValue);
    upsertLink('canonical', resolvedCanonical);

    upsertMeta('property', 'og:title', resolvedOgTitle);
    upsertMeta('property', 'og:description', resolvedOgDesc);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('property', 'og:image:width', '1200');
    upsertMeta('property', 'og:image:height', '630');
    upsertMeta('property', 'og:url', resolvedOgUrl);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', 'Bolão Elite');
    upsertMeta('property', 'og:locale', OG_LOCALE[locale] ?? 'pt_BR');
    upsertHtmlLang(locale);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', resolvedOgTitle);
    upsertMeta('name', 'twitter:description', resolvedOgDesc);
    upsertMeta('name', 'twitter:image', ogImage);

    if (googleVerificationCode) {
      upsertMeta('name', 'google-site-verification', googleVerificationCode);
    }
  }, [
    title,
    description,
    keywords,
    robotsValue,
    resolvedCanonical,
    resolvedOgTitle,
    resolvedOgDesc,
    ogImage,
    resolvedOgUrl,
    googleVerificationCode,
    locale,
  ]);

  // Release C: hreflang alternates (substitui o conjunto a cada navegação).
  useEffect(() => {
    document.head
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach((el) => el.remove());
    if (!alternates || alternates.length === 0) return;
    for (const alt of alternates) {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', alt.hreflang);
      link.setAttribute('href', alt.href);
      document.head.appendChild(link);
    }
    return () => {
      document.head
        .querySelectorAll('link[rel="alternate"][hreflang]')
        .forEach((el) => el.remove());
    };
  }, [alternates]);

  // Injeta JSON-LD structured data no <head>
  useEffect(() => {
    if (!jsonLd) return;
    const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    const ids: string[] = [];

    schemas.forEach((schema, i) => {
      const id = `seo-ld-${i}`;
      ids.push(id);
      let el = document.getElementById(id) as HTMLScriptElement | null;
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        el.type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.text = JSON.stringify(schema);
    });

    return () => {
      ids.forEach(id => document.getElementById(id)?.remove());
    };
  }, [jsonLd]);

  return null;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector(
    `meta[${attr}="${key}"]`
  ) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertHtmlLang(locale: string) {
  document.documentElement.setAttribute('lang', locale);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
